import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Check, Loader2, TriangleAlert } from "lucide-react"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { SelectorDeFecha } from "@/components/SelectorDeFecha"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  COST_BEHAVIOR_LABELS,
  PAYROLL_REGIME_LABELS,
  PERSON_NATURE_LABELS,
  type Client,
  type CostCenter,
  type CostBehavior,
  type PayrollRegime,
  type Person,
  type PersonNature,
} from "@/lib/types"
import { useAuth } from "@/lib/auth"
import { REGIMENES, ayudaRegimen, etiquetaRegimen, tieneNormativaPropia } from "@/lib/regimenes"
import { fetchLineasDeLaHoja } from "@/routes/finance/api"

const SIN_LINEA = "__sin_linea__"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const schema = z
  .object({
    /*
      LA SOCIEDAD, Y VA PRIMERO. Cesar: «las personas deberían estar asociadas a
      cada sociedad […] actualmente cuando cargas no elijes a qué sociedad se
      carga». Se inyectaba en silencio desde el selector de la barra de arriba
      —`mapping.ts` lo admitía: «SIN CONTRAPARTE en la SPA»—, así que el destino
      del alta dependía de un desplegable que está en otra parte de la pantalla.
      Ver `lib/regimenes.ts`.
    */
    entityId: z.string().min(1, "Elige la sociedad a la que entra esta persona."),
    fullName: z.string().min(5, "Nombre completo obligatorio."),
    docNumber: z.string().min(6, "Documento de identidad obligatorio."),
    email: z.string().email("Correo no válido.").or(z.literal("")),
    position: z.string().min(3, "Cargo obligatorio."),
    costCenterId: z.string().min(1, "El area / centro de costo es obligatorio."),
    nature: z.enum(["costo_venta", "gasto_operativo", "capex"], {
      required_error: "La naturaleza es obligatoria: define el margen bruto.",
    }),
    behavior: z.enum(["fijo", "variable", "semi_variable"], {
      required_error: "El comportamiento es obligatorio.",
    }),
    regime: z.enum(["planilla", "recibo_honorarios"], {
      required_error: "El regimen es obligatorio.",
    }),
    billable: z.boolean(),
    clientIds: z.array(z.string()),
    startDate: z.string().min(1, "Fecha de ingreso obligatoria."),
    /** Vacío = sigue activa. Con fecha, es una baja (Cesar: «poner la fecha de cierre»). */
    endDate: z.string(),
    /** Migración 0038. Vacío = la línea de «Labor Costs» que diga el régimen. */
    lineaHoja: z.string(),
    salaryBand: z.string().min(1, "Banda salarial obligatoria."),
    monthlyGross: z
      .string()
      .min(1, "Remuneracion bruta obligatoria.")
      .refine((value) => Number(value) > 0, "Debe ser un numero mayor a cero."),
    plannedRaise: z.string(),
    reason: z.string().min(10, "El motivo es obligatorio (minimo 10 caracteres)."),
  })
  .superRefine((values, ctx) => {
    if (values.billable && values.clientIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["clientIds"],
        message: "Una persona facturable debe estar asignada al menos a un cliente.",
      })
    }
    if (values.nature === "costo_venta" && !values.billable) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["billable"],
        message:
          "Si es costo de ventas normalmente es facturable. Marca facturable o cambia la naturaleza.",
      })
    }
  })

export type PersonFormValues = z.infer<typeof schema>

const NATURE_HELP: Record<PersonNature, string> = {
  costo_venta:
    "Gente asignada a la entrega del servicio al cliente. Entra al costo de ventas y baja el margen bruto.",
  gasto_operativo:
    "Estructura. Entra debajo del margen bruto: afecta el margen operativo y el EBITDA, no el margen bruto.",
  capex:
    "Se capitaliza: no golpea el P&L del mes, se activa y se deprecia. Solo para desarrollo de un activo propio identificable.",
}

/** Donde cae cada naturaleza en el P&L. La consecuencia, no la etiqueta. */
const PL_LINES: { key: string; label: string; strong?: boolean }[] = [
  { key: "ingresos", label: "Ventas" },
  { key: "costo_venta", label: "− Costo de ventas" },
  { key: "margen_bruto", label: "= Margen bruto", strong: true },
  { key: "gasto_operativo", label: "− Gasto operativo" },
  { key: "ebitda", label: "= EBITDA", strong: true },
]

const NATURE_LANDS_ON: Record<PersonNature, string | null> = {
  costo_venta: "costo_venta",
  gasto_operativo: "gasto_operativo",
  capex: null,
}

const REGIME_HELP: Record<PayrollRegime, string> = {
  planilla: "Trae EsSalud 9%, CTS (may/nov), gratificaciones (jul/dic), vacaciones y SCTR.",
  recibo_honorarios: "Retencion de 4ta categoria del 8% y ninguna carga social.",
}

interface PersonFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  person: Person | null
  costCenters: CostCenter[]
  clients: Client[]
  pending?: boolean
  onSubmit: (values: PersonFormValues) => void | Promise<void>
}

const BLANK: PersonFormValues = {
  fullName: "",
  docNumber: "",
  position: "",
  costCenterId: "",
  entityId: "",
  nature: "costo_venta",
  behavior: "fijo",
  regime: "planilla",
  billable: true,
  clientIds: [],
  startDate: "",
  endDate: "",
  lineaHoja: "",
  email: "",
  salaryBand: "",
  monthlyGross: "",
  plannedRaise: "",
  reason: "",
}

/**
 * Alta y edicion de personas.
 *
 * La naturaleza (costo de ventas / gasto operativo / capex) es el campo mas
 * consecuente de todo el maestro: clasificar mal a una persona mueve el margen
 * bruto varios puntos. Por eso NO es un select en una fila de tres: es el
 * bloque central del formulario, con las tres opciones abiertas, la explicacion
 * de cada una y un esquema del P&L que muestra donde va a caer el costo.
 * El coste visual es deliberado — debe verse tan consecuente como es.
 */
export function PersonFormDialog({
  open,
  onOpenChange,
  person,
  costCenters,
  clients,
  pending,
  onSubmit,
}: PersonFormDialogProps) {
  // Las sociedades salen del contexto y no de una prop: el diálogo las necesita
  // SIEMPRE, y pasarlas por prop obligaría a que cada pantalla que lo monte se
  // acuerde — que es la forma de que una se olvide y el campo salga vacío.
  const { entities, activeEntityId } = useAuth()
  const entidadSugerida = activeEntityId
  const form = useForm<PersonFormValues>({
    resolver: zodResolver(schema),
    defaultValues: BLANK,
  })

  React.useEffect(() => {
    if (!open) return
    form.reset(
      person
        ? {
            fullName: person.fullName,
            docNumber: person.docNumber,
            position: person.position,
            entityId: person.entityId ?? "",
            costCenterId: person.costCenterId,
            nature: person.nature,
            behavior: person.behavior,
            regime: person.regime,
            billable: person.billable,
            clientIds: person.clientIds,
            startDate: person.startDate,
            endDate: person.endDate ?? "",
            lineaHoja: person.lineaHoja ?? "",
            email: person.email ?? "",
            salaryBand: person.salaryBand,
            monthlyGross: String(person.monthlyGross),
            plannedRaise: person.plannedRaise ?? "",
            reason: "",
          }
        : {
            ...BLANK,
            /*
              EN EL ALTA SE PROPONE la sociedad de la barra, pero como VALOR de
              un campo visible y no como inyección invisible. La diferencia no es
              cosmética: propuesto se puede corregir antes de guardar y se ve al
              revisar; inyectado no se ve nunca. Ver `lib/regimenes.ts`.
            */
            entityId: entidadSugerida ?? "",
          },
    )
  }, [open, person, form, entidadSugerida])

  const billable = form.watch("billable")
  const nature = form.watch("nature")
  const regime = form.watch("regime")
  /*
    EL PAÍS SIGUE A LA SOCIEDAD DEL FORMULARIO, no a la de la barra de arriba.
    Son dos cosas distintas en cuanto alguien cambia el campo: si siguiera a la
    barra, elegir la sociedad brasileña aquí dejaría las etiquetas peruanas.
  */
  const entityId = form.watch("entityId")
  const paisDeLaSociedad = entities.find((e) => e.id === entityId)?.country ?? null
  const lineasLabor = useQuery({ queryKey: ["lineas-hoja", "Labor"], queryFn: () => fetchLineasDeLaHoja("Labor Costs") })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {person ? `Editar persona · ${person.fullName}` : "Alta de persona"}
          </DialogTitle>
          <DialogDescription>
            {t("Estos campos gobiernan el margen bruto, el payroll proyectado y el calendario de caja.")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(async (values) => {
              await onSubmit(values)
            })}
            className="space-y-4"
          >
            {/*
              LA SOCIEDAD, SOLA Y ARRIBA DEL TODO.

              No va metida en la fila de «Identificación» a propósito: es la
              decisión que determina bajo QUÉ NORMA entra esta persona, y de ella
              dependen los nombres del régimen que se ven más abajo. Un
              desplegable más entre el DNI y el cargo se rellena sin leerlo.

              En edición NO se puede cambiar: mover a alguien de sociedad no es
              corregir un campo, es una baja y un alta —cambia su planilla, sus
              aportes y su histórico—, y hacerlo con un `PATCH` dejaría los
              asientos anteriores colgando de la sociedad de antes.
            */}
            <fieldset>
              <legend className="eyebrow mb-1.5">{t("A qué sociedad entra")}</legend>
              <FormField
                control={form.control}
                name="entityId"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={Boolean(person)}
                    >
                      <FormControl>
                        <SelectTrigger className="sm:max-w-md">
                          <SelectValue placeholder={t("Elige la sociedad")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {entities.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            <span className="flex items-baseline gap-2">
                              <span className="truncate">{e.name || e.id}</span>
                              {e.country ? (
                                <span className="code shrink-0 text-2xs text-muted-foreground">
                                  {e.country.toUpperCase()}
                                </span>
                              ) : null}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {person
                        ? "La sociedad no se cambia editando: mover a alguien es una baja y un alta."
                        : paisDeLaSociedad && !tieneNormativaPropia(paisDeLaSociedad)
                          ? `Todavía no tengo la normativa laboral de ${paisDeLaSociedad}: guardo bien los datos, pero los aportes de este país no los calculo.`
                          : "De ella dependen el régimen, los aportes y en qué planilla entra."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            {/* ---- Identificacion ---- */}
            <fieldset className="grid gap-3 sm:grid-cols-3">
              <legend className="eyebrow mb-1.5">{t("Identificacion")}</legend>
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t("Nombre completo *")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t("Nombres y apellidos")} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="docNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("DNI / CE *")}</FormLabel>
                    <FormControl>
                      <Input {...field} className="font-mono" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Correo")}</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="nombre@empresa.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Cargo *")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="costCenterId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Area / centro de costo *")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("Selecciona el area")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {costCenters.map((costCenter) => (
                          <SelectItem key={costCenter.id} value={costCenter.id}>
                            <span className="code mr-1.5">{costCenter.code}</span>
                            {costCenter.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t("Agrupa el gasto y el P&L por area.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Fecha de ingreso (contrato) *")}</FormLabel>
                    <FormControl>
                      <SelectorDeFecha
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Fecha de salida (baja)")}</FormLabel>
                    <FormControl>
                      <SelectorDeFecha
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            {/* ---- LA decision: naturaleza del costo ---- */}
            <FormField
              control={form.control}
              name="nature"
              render={({ field }) => (
                <FormItem className="rounded-panel border-2 border-brand/35 bg-brand-soft/40 p-3">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <div>
                      <FormLabel className="text-2xs font-semibold text-foreground">
                        {t("Naturaleza del costo * — define el margen bruto")}</FormLabel>
                      <FormDescription className="mt-0.5">
                        {t("En una empresa de servicios el margen bruto es la gente asignada a clientes. Clasificar mal a una persona lo mueve varios puntos.")}</FormDescription>
                    </div>
                  </div>

                  <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_11rem]">
                    <div
                      role="radiogroup"
                      aria-label={t("Naturaleza del costo")}
                      className="grid gap-2 sm:grid-cols-3"
                    >
                      {(Object.keys(PERSON_NATURE_LABELS) as PersonNature[]).map((option) => {
                        const selected = field.value === option
                        return (
                          <button
                            key={option}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => field.onChange(option)}
                            className={cn(
                              "group/opt relative rounded-md border p-2.5 text-left transition-all duration-100",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                              selected
                                ? "border-brand bg-surface shadow-sm ring-1 ring-brand"
                                : "border-input bg-surface/70 hover:border-border-strong hover:bg-surface",
                            )}
                          >
                            <span
                              className={cn(
                                "absolute right-2 top-2 grid h-4 w-4 place-items-center rounded-full border transition-colors",
                                selected
                                  ? "border-brand bg-brand text-brand-foreground"
                                  : "border-border-strong bg-transparent text-transparent",
                              )}
                              aria-hidden="true"
                            >
                              <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                            </span>
                            <div className="pr-5 text-xs font-semibold text-foreground">
                              {PERSON_NATURE_LABELS[option]}
                            </div>
                            <p className="mt-1 text-2xs leading-4 text-muted-foreground">
                              {NATURE_HELP[option]}
                            </p>
                          </button>
                        )
                      })}
                    </div>

                    {/* Consecuencia: donde cae en el P&L. */}
                    <PLPreview nature={field.value} />
                  </div>

                  {nature === "capex" ? (
                    <p className="mt-2 flex items-start gap-1.5 rounded-md border border-warning/35 bg-warning-soft p-2 text-2xs leading-4">
                      <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
                      <span>
                        <strong>{t("Capex.")}</strong> {" "}{t("El costo de esta persona no golpea el P&L del mes: se activa y se deprecia. Usalo solo cuando desarrolla un activo propio identificable — si no, estas inflando el resultado.")}</span>
                    </p>
                  ) : null}

                  <FormMessage />
                </FormItem>
              )}
            />

            {/* ---- Comportamiento y regimen ---- */}
            <fieldset className="grid gap-3 sm:grid-cols-2">
              <legend className="eyebrow mb-1.5">{t("Comportamiento y regimen")}</legend>
              <FormField
                control={form.control}
                name="behavior"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Comportamiento *")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(Object.keys(COST_BEHAVIOR_LABELS) as CostBehavior[]).map((option) => (
                          <SelectItem key={option} value={option}>
                            {COST_BEHAVIOR_LABELS[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t("Apalancamiento operativo en la proyeccion.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="regime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Regimen *")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      {/*
                        LAS OPCIONES LAS PONE EL PAÍS DE LA SOCIEDAD. Estaban
                        fijas en «Planilla» y «Recibo por honorarios (4ta)»: la
                        4ta es la cuarta categoría de renta PERUANA, y se le
                        ofrecía a las cuatro sociedades. Ver `lib/regimenes.ts`.
                      */}
                      <SelectContent>
                        {REGIMENES.map((option) => (
                          <SelectItem key={option} value={option}>
                            {etiquetaRegimen(option, paisDeLaSociedad)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{ayudaRegimen(regime, paisDeLaSociedad)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/*
                LA LÍNEA DE LA HOJA. Cesar, 2026-10-05: el desglose de «costo de
                equipo» abre cada línea por persona. Sin elegir nada manda el
                régimen (planilla → «Net Salaries», honorarios → «RxH»); elegir
                una es para las excepciones (comisiones, Deel…).
              */}
              <FormField
                control={form.control}
                name="lineaHoja"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Línea de la hoja")}</FormLabel>
                    <Select value={field.value || SIN_LINEA}
                            onValueChange={(v) => field.onChange(v === SIN_LINEA ? "" : v)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={SIN_LINEA}>{t("Según el régimen")}</SelectItem>
                        {(lineasLabor.data?.lineas ?? []).map((l) => (
                          <SelectItem key={l} value={l}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t("En qué línea de «Costo de equipo» de tu hoja cae. Vacío: la que diga el régimen.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            {/* ---- Facturabilidad ---- */}
            <div className="rounded-md border bg-surface-sunken/50 p-3">
              <FormField
                control={form.control}
                name="billable"
                render={({ field }) => (
                  <FormItem>
                    <label className="flex cursor-pointer items-start gap-2 text-xs">
                      <Checkbox
                        className="mt-px"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                      />
                      <span>
                        <span className="font-medium">{t("Es facturable a clientes")}</span>
                        <FormDescription className="mt-0.5">
                          {t("La asignacion a clientes es lo que permite margen bruto por cliente.")}</FormDescription>
                      </span>
                    </label>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {billable ? (
                <FormField
                  control={form.control}
                  name="clientIds"
                  render={({ field }) => (
                    <FormItem className="pt-2.5">
                      <FormLabel>{t("Clientes asignados *")}</FormLabel>
                      <div className="grid gap-1 pt-1 sm:grid-cols-2 lg:grid-cols-3">
                        {clients.map((client) => {
                          const checked = field.value.includes(client.id)
                          return (
                            <label
                              key={client.id}
                              className={cn(
                                "flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-xs transition-colors",
                                checked
                                  ? "border-brand/40 bg-brand-soft"
                                  : "border-input bg-surface hover:border-border-strong",
                              )}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(next) => {
                                  field.onChange(
                                    next
                                      ? [...field.value, client.id]
                                      : field.value.filter((id: string) => id !== client.id),
                                  )
                                }}
                              />
                              <span className="truncate">{client.name}</span>
                            </label>
                          )
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </div>

            {/* ---- Remuneracion ---- */}
            <fieldset className="grid gap-3 sm:grid-cols-3">
              <legend className="eyebrow mb-1.5">{t("Remuneracion")}</legend>
              <FormField
                control={form.control}
                name="salaryBand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Banda salarial *")}</FormLabel>
                    <FormControl>
                      <Input {...field} className="font-mono" placeholder={t("B3")} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="monthlyGross"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Bruto mensual *")}</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="decimal" className="text-right font-mono" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="plannedRaise"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Aumento previsto")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="2027-01 · +6%" />
                    </FormControl>
                    <FormDescription>{t("Alimenta el payroll proyectado.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Motivo *")}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t("Contratacion aprobada, cambio de area, reasignacion de cliente...")}
                      className="min-h-[4rem]"
                    />
                  </FormControl>
                  <FormDescription>
                    {t("Obligatorio en toda alta y edicion. Queda con autor y fecha en el audit log.")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("Cancelar")}</Button>
              <Button type="submit" size="sm" disabled={pending}>
                {pending ? <Loader2 className="animate-spin" /> : null}
                {person ? "Guardar cambios" : "Dar de alta"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

/** Esquema del P&L con la linea donde cae el costo de esta persona resaltada. */
function PLPreview({ nature }: { nature: PersonNature }) {
  const lands = NATURE_LANDS_ON[nature]
  return (
    <aside className="rounded-md border bg-surface p-2">
      <div className="eyebrow mb-1.5">{t("Donde cae")}</div>
      <ol className="space-y-px">
        {PL_LINES.map((line) => {
          const active = line.key === lands
          return (
            <li
              key={line.key}
              className={cn(
                "flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-2xs transition-colors",
                line.strong && "font-semibold",
                active
                  ? "bg-brand text-brand-foreground"
                  : line.strong
                    ? "text-foreground"
                    : "text-muted-foreground",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "h-2.5 w-[2px] rounded-full",
                  active ? "bg-brand-foreground" : "bg-transparent",
                )}
              />
              {line.label}
            </li>
          )
        })}
      </ol>
      {lands === null ? (
        <p className="mt-1.5 border-t pt-1.5 text-2xs leading-4 text-muted-foreground">
          {t("No entra al P&L del mes: va al activo y se deprecia.")}</p>
      ) : null}
    </aside>
  )
}
